import type { SceneElement } from './types.js';

/** Location of an existing artwork pin, relative to the map's longest side.
 * Imported maps and their pins are adjacent in paint order, including when two
 * masked maps occupy the same box. Proximity alone cannot identify those pairs.
 * This is picker geometry only; it does not mutate the scene or create a pin.
 */
export function mapPickerPin(elements: readonly SceneElement[], mapId: string): {
  x: number;
  y: number;
  artwork?: { elementId: string; assetId?: string; w: number; h: number };
} | undefined {
  const ordered = [...elements].sort((a, b) => a.z - b.z);
  const index = ordered.findIndex((el) => el.id === mapId && el.type === 'map');
  const map = ordered[index];
  if (!map || map.type !== 'map') return undefined;
  const pin = ordered[index + 1];
  if (
    map.imported?.system === 'customily' &&
    pin?.type === 'image' && pin.imported?.system === 'customily' &&
    /\bpin\b/i.test(pin.name ?? '') && pin.visible !== false &&
    (pin.fit === undefined || pin.fit === 'contain') &&
    pin.groupId === map.groupId &&
    !map.transform.rotation && !pin.transform.rotation &&
    !map.transform.skewX && !map.transform.skewY &&
    !pin.transform.skewX && !pin.transform.skewY &&
    !pin.transform.flipX && !pin.transform.flipY
  ) {
    const size = Math.max(map.transform.w, map.transform.h);
    if (!(size > 0)) return undefined;
    // Start at the box's bottom centre. The picker refines this to the visible
    // tip after loading the selected artwork (imported PNGs have padding).
    const x = (pin.transform.cx - map.transform.cx) / size;
    const y = (pin.transform.cy + pin.transform.h / 2 - map.transform.cy) / size;
    if (Math.abs(x) <= 0.5 && Math.abs(y) <= 0.5) return {
      x, y,
      artwork: {
        elementId: pin.id,
        assetId: typeof pin.value === 'string' ? pin.value
          : pin.source.kind === 'static' ? pin.source.assetId : pin.sample?.assetId,
        w: pin.transform.w / size,
        h: pin.transform.h / size,
      },
    };
  }
  // The renderer's built-in heart/dot is centred on the location.
  return map.config.marker !== null ? { x: 0, y: 0 } : undefined;
}
